import { Injectable, signal } from "@angular/core";
import { environment } from '../../../environments/environment';
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable, finalize, map, of, shareReplay, tap } from "rxjs";
import { Router } from '@angular/router';

@Injectable({
    providedIn: 'root',
})
export class UserHttpService {
    // Legacy localStorage token keys; remove after cookie-only auth is fully verified.
    // private readonly accessTokenKey = 'apiSnfKey';
    // private readonly refreshTokenKey = 'apiSnfRefreshKey';

    constructor(
        public httpClient: HttpClient,
        private router: Router,
    ) { }

    public apiUrl: string = environment.apiUrl;

    currentUser = signal<any | null>(null);
    private currentUserRequest$: Observable<any> | null = null;


    login(payload: any, recaptchaToken: string): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/login`, payload, {
            headers: new HttpHeaders({ 'x-recaptcha-token': recaptchaToken }),
        });
    }

    getCurrentUser(): Observable<any> {
        const currentUser = this.currentUser();
        if (currentUser) {
            return of(currentUser);
        }

        if (this.currentUserRequest$) {
            return this.currentUserRequest$;
        }

        this.currentUserRequest$ = this.httpClient.get(`${this.apiUrl}/auth/me`).pipe(
            map((res: any) => res?.user ?? null),
            tap((user) => this.currentUser.set(user)),
            finalize(() => {
                this.currentUserRequest$ = null;
            }),
            shareReplay(1),
        );

        return this.currentUserRequest$;
    }

    setCurrentUser(user: any | null): void {
        this.currentUser.set(user);
    }

    getCsrfToken(): Observable<any> {
        return this.httpClient.get(`${this.apiUrl}/auth/csrf`);
    }

    forgetPassword(email: string): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/password-reset/request`, { email, from: 'frontend' });
    }

    resetPassword(token: string, password: string): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/password-reset/confirm`, { token, password });
    }

    oAuthLogin(provider: 'google', credential: string): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/oauth/${provider}`, { credential });
    }

    removeOAuth(provider: string): Observable<any> {
        return this.httpClient.delete(`${this.apiUrl}/auth/oauth/${provider}`);
    }

    verify2FALogin(userId: string, token: string): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/2fa/2fa-login`, { userId, token });
    }

    request2FARecovery(userId: string): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/2fa/recovery/request`, { userId });
    }

    verify2FARecovery(userId: string, otp: string): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/2fa/recovery/verify`, { userId, otp });
    }

    requestOtp(): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/otp`, {});
    }

    verifyOtp(otp: string): Observable<any> {
        return this.httpClient.patch(`${this.apiUrl}/auth/verify`, { otp });
    }

    getProfiles(page?: number, perPage?: number, searchBy?: string, searchTerm?: string): Observable<any> {
        let params = new HttpParams()
            .set('page', String(page ?? 1))
            .set('perPage', String(perPage ?? 10));

        if (searchBy) {
            params = params.set('searchBy', searchBy);
        }

        const trimmedSearchTerm = (searchTerm ?? '').trim();
        if (trimmedSearchTerm) {
            params = params.set('searchTerm', trimmedSearchTerm);
        }

        return this.httpClient.get(`${this.apiUrl}/auth/profiles`, { params });
    }

    exportProfilesExcel(): Observable<Blob> {
        return this.httpClient.get(`${this.apiUrl}/auth/profiles/export/excel`, {
            responseType: 'blob',
        });
    }

    getProfile(id: string): Observable<any> {
        return this.httpClient.get(`${this.apiUrl}/auth/profile/${id}`);
    }

    signup(payload: any, recaptchaToken: string): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/signup`, payload, {
            headers: new HttpHeaders({ 'x-recaptcha-token': recaptchaToken }),
        });
    }

    updateProfile(id: string, payload: any): Observable<any> {
        return this.httpClient.patch(`${this.apiUrl}/auth/profile/${id}`, payload);
    }

    deleteCompanyLogo(id: string): Observable<any> {
        return this.httpClient.delete(`${this.apiUrl}/auth/profile/${id}/company-logo`);
    }

    changePassword(id: string, payload: { current_password?: string; new_password: string; confirm_password: string }): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/reset-password-when-login/${id}`, payload);
    }

    deleteProfile(id: string): Observable<any> {
        return this.httpClient.delete(`${this.apiUrl}/auth/profile/${id}`);
    }

    softDeleteProfile(id: string): Observable<any> {
        return this.httpClient.delete(`${this.apiUrl}/auth/profile/${id}/soft`);
    }

    enable2FA(): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/2fa/2fa-enable`, {});
    }

    verify2FASetup(token: string): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/2fa/2fa-verify-setup`, { token });
    }

    disable2FA(payload: { current_password?: string; token: string }): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/2fa/2fa-disable`, payload);
    }

    request2FABackupEmail(backup_email: string): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/2fa/backup-email/request`, { backup_email });
    }

    verify2FABackupEmail(otp: string): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/2fa/backup-email/verify`, { otp });
    }

    verify2FABackupEmailToken(id: string, token: string): Observable<any> {
        return this.httpClient.get(`${this.apiUrl}/auth/2fa/backup-email/verify-token/${id}/${token}`);
    }

    patchCurrentUser(
        patch: Partial<{
            full_name: string;
            email: string;
            user_type: string;
            role: string;
            verified: boolean;
            two_factor_enabled: boolean;
            two_factor_backup_email: string;
            two_factor_backup_email_verified: boolean;
            _id: string;
            auth_provider: string;
            oauth_provider: string | null;
        }>,
    ): void {
        const current = this.currentUser();
        if (!current) {
            return;
        }
        this.currentUser.set({ ...current, ...patch });
    }



    refreshToken(): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/refresh`, {});
    }


    // Legacy localStorage token helpers; remove after cookie-only auth is fully verified.
    // setTokens(accessToken: string, refreshToken?: string): void {
    //     localStorage.setItem(this.accessTokenKey, accessToken);
    //     if (refreshToken) {
    //         localStorage.setItem(this.refreshTokenKey, refreshToken);
    //     } else {
    //         localStorage.removeItem(this.refreshTokenKey);
    //     }
    //     this.restoreUser();
    // }
    //
    // setToken(token: string): void {
    //     this.setTokens(token);
    // }
    //
    // getToken(): string | null {
    //     return localStorage.getItem(this.accessTokenKey);
    // }
    //
    // getRefreshToken(): string | null {
    //     return localStorage.getItem(this.refreshTokenKey);
    // }

    isCurrentUserVerified(): boolean {
        const verified = this.currentUser()?.verified;
        return verified === true || verified === 'true' || verified === 1 || verified === '1';
    }

    refreshAccessToken(): Observable<void> {
        return this.refreshToken().pipe(
            map(() => undefined),
        );
    }

    logout(): void {
        this.currentUser.set(null);
        this.currentUserRequest$ = null;
        // Navigate immediately. The logout request only clears server cookies and must not
        // leave an expired session on a protected screen while it is in flight.
        void this.router.navigate(['/login']);
        this.httpClient.post(`${this.apiUrl}/auth/logout`, {}).subscribe({
            next: () => this.currentUser.set(null),
            error: () => this.currentUser.set(null),
        });
    }

    // Legacy JWT restore; remove after cookie-only auth is fully verified.
    // restoreUser(): void {
    //     const token = this.getToken();
    //     if (!token) {
    //         this.currentUser.set(null);
    //         return;
    //     }
    //
    //     try {
    //         const payload: any = jwtDecode(token);
    //         this.currentUser.set(payload);
    //     } catch {
    //         this.logout();
    //     }
    // }

    sampleReportRequest(payload: any): Observable<any> {
        return this.httpClient.post(`${this.apiUrl}/auth/sample-report`, payload);
    }
}
